import React, { useCallback, useMemo, useState, useRef } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { Autocomplete } from "../UI/Autocomplete";
import { Table } from "../UI/Table";
import { StyledRightContainer } from "../Projects/styles";
import { ResourceLabelContent } from "../User/ResourceLabelContent";
import { useAutoCompleteOptions } from "../../utils/hooks/useAutoCompleteOptions";
import { useToastInfo } from "../../utils/hooks/useToastInfo";
import { useFetch } from "../../utils/hooks/useFetch";
import { getAuthHeaders } from "../../utils/services/auth";
import { Loader } from "../Loader/Loader";
import { Alert } from "../UI/Alert";
import { DatePickerInput } from "../UI/DatePicker";
import {
  FETCH_ACCOUNTS_BY_QUERY_URL,
  FETCH_REPORTS_LIST,
  FETCH_FILE_DATA,
} from "../../utils/services/constants";
import { translate } from "../../utils/translations/translate";
import { DATE_TIME_FORMAT } from "../../utils/constants";
import "./styles.css";

const Checkbox = ({ checked, onChange, style }) => (
  <input type="checkbox" checked={checked} onChange={onChange} style={style} />
);

export const ReportsTab = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [resourceCreatedOn, setResourceCreatedOn] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const gridRef = useRef(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const { toastInfo, setToastInfo } = useToastInfo();

  const { userInfo } = useSelector((store) => store.user).authInfo || {};
  const { CreatedOn } = userInfo ?? {};

  const {
    isLoading: isResourceOptionsLoading,
    options: resourceDrpDwnOptions,
    searchKey: resourceKey,
    updateSearchKey: updateResourceKey,
  } = useAutoCompleteOptions(
    FETCH_ACCOUNTS_BY_QUERY_URL,
    "Admin=true&Approver=true&Employee=true&Search",
    false
  );

  const handleToastClose = () => {
    setToastInfo(null);
  };

  const { performAction } = useFetch();

  const formatShortDate = (date) =>
    date.toLocaleDateString("en-US", { month: "short", day: "2-digit" });

  const formatFullDate = (date) => date?.toISOString()?.split("T")[0];

  const generateWeeklyRanges = useCallback(() => {
    if (selectedWeeks?.length) return [];

    const startDate = new Date(CreatedOn);
    const today = new Date();
    const weeks = [];

    let currentMonday = new Date(startDate);
    const day = currentMonday.getDay();
    if (day !== 1) {
      const diff = (day === 0 ? -6 : 1) - day;
      currentMonday.setDate(currentMonday.getDate() + diff);
    }

    while (currentMonday <= today) {
      const startOfWeek = new Date(currentMonday);
      const endOfWeek = new Date(currentMonday);
      endOfWeek.setDate(endOfWeek.getDate() + 6);

      weeks.push({
        startFullDate: formatFullDate(startOfWeek),
        endFullDate: formatFullDate(endOfWeek),
        label: `${formatShortDate(startOfWeek)} - ${formatShortDate(
          endOfWeek
        )}`,
        selected: false,
      });

      currentMonday.setDate(currentMonday.getDate() + 7);
    }

    weeks.reverse();
    weeks.unshift({ label: "Select All", selected: false });

    return weeks;
  }, [CreatedOn, selectedWeeks]);

  useMemo(() => {
    if (resourceCreatedOn === null) return false;
    const weeks = generateWeeklyRanges();
    if (weeks.length) {
      setSelectedWeeks(weeks);
    }
  }, [generateWeeklyRanges, resourceCreatedOn]);

  const getFormattedOptions = useCallback(
    (list) =>
      list.map((option) => ({
        label: option.ResourceName,
        secondaryLabel: `${option.Email} , ${option.AccountType}`,
        value: option.ResourceID,
        optionRef: option,
      })),
    []
  );

  const formattedResourceDrpDwnOptions = useMemo(
    () => getFormattedOptions(resourceDrpDwnOptions),
    [resourceDrpDwnOptions, getFormattedOptions]
  );

  const handleSelection = (value) => {
    if (!value) {
      updateResourceKey({ target: { value: "" } });
      setSelectedResource(null);
    } else {
      updateResourceKey({ target: { value: value.label } });
      setSelectedResource(value);
      setResourceCreatedOn(value.optionRef.CreatedOn);
    }
  };

  const handleCheckboxChange = (option) => {
    const updatedWeeks = selectedWeeks.map((item) => {
      if (option.label === "Select All") {
        return { ...item, selected: !selectedWeeks[0].selected };
      }
      if (item.label === option.label) {
        return { ...item, selected: !item.selected };
      }
      return item;
    });

    setSelectedWeeks(updatedWeeks);
  };

  const handleweekSelection = (value) => {
    if (!value) {
      setSelectedWeeks((prev) =>
        prev.map((item) => ({ ...item, selected: false }))
      );
    } else {
      handleCheckboxChange(value);
    }
  };

  const formatReportData = useCallback(
    (data) =>
      data.map((item, index) => ({
        id: index,
        resourceName: item.ResourceName,
        resourceId: item.ResourceID,
        projectId: item.ProjectID,
        projectName: item.ProjectName,
        approverName: item.ApproverName,
        startDate: item.StartDate
          ? moment(item.StartDate).format(DATE_TIME_FORMAT.DISPLAY)
          : "N/A",
        endDate: item.EndDate
          ? moment(item.EndDate).format(DATE_TIME_FORMAT.DISPLAY)
          : "N/A",
        attachments: item.Attachments,
      })),
    []
  );

  const handleSearch = async () => {
    setIsLoading(true);
    // const startDates = selectedWeeks
    //   .filter((week) => week.selected && week.startFullDate)
    //   .map((week) => week.startFullDate)
    //   .join(",");

    // const endDates = selectedWeeks
    //   .filter((week) => week.selected && week.endFullDate)
    //   .map((week) => week.endFullDate)
    //   .join(",");

    const resourceId = selectedResource?.value || "";

    const queryParams = new URLSearchParams({
      StartDate: startDate,
      EndDate: endDate,
      ResourceID: resourceId,
      PageNo: page,
      PageSize: pageSize,
    }).toString();

    try {
      const response = await performAction(
        `${FETCH_REPORTS_LIST}?${queryParams}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      if (Array.isArray(response?.Result)) {
        const formattedData = formatReportData(response.Result);
        setRowData(formattedData);
        setTotalRecords(formattedData[0]?.TotalRecords || 0);
        setIsLoading(false);
      } else {
        setRowData([]);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setIsLoading(false);
      setRowData([]);
    }
  };

  const downloadFile = async (fileName) => {
    try {
      const response = await performAction(
        `${FETCH_FILE_DATA}?FileName=${fileName}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      const blob = await response.blob();
      const finalFileName = fileName.split("/").pop();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = finalFileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error fetching file:", error);
      // setRowData([]); // Optional: handle error state
    }
  };

  const handleDownload = async () => {
    if (!selectedRecords.length) {
      setToastInfo({
        message: "Please select atleast one record",
        type: "error",
        autoExpire: true,
      });
      return false;
    }
    const filteredData = rowData.filter((item) =>
      selectedRecords.includes(item.id)
    );
    const mergedNames = filteredData
      .map((item) => item.attachments)
      .join(",")
      .split(",")
      .filter((item) => item !== "");

    if (mergedNames.length === 0) {
      setToastInfo({
        message: "Attachments are not available for selected week(s).",
        type: "error",
        autoExpire: true,
      });
      return false;
    }
    setIsLoading(true);

    for (const file of mergedNames) {
      if (file) {
        await downloadFile(file);
      }
    }
    setIsLoading(false);
  };

  const handlePaginationChange = useCallback(({ page, pageSize }) => {
    setPageNumber(page);
    setPageSize(pageSize);
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: translate("labels.resourceId"),
        field: "resourceId",
        sortable: true,
        filter: false,
        width: 150,
      },
      {
        headerName: translate("labels.resourceName"),
        field: "resourceName",
        sortable: true,
        filter: false,
        width: 150,
      },
      {
        headerName: translate("labels.projectId"),
        field: "projectId",
        sortable: true,
        filter: false,
        width: 100,
      },
      {
        headerName: translate("labels.projectName"),
        field: "projectName",
        sortable: true,
        filter: false,
        width: 100,
      },
      {
        headerName: translate("labels.approverName"),
        field: "approverName",
        sortable: true,
        filter: false,
        width: 100,
      },
      {
        headerName: translate("labels.week"),
        field: "week",
        valueGetter: (params) =>
          `${params.data.startDate} - ${params.data.endDate}`,
        sortable: true,
        filter: false,
        width: 200,
      },
    ],
    []
  );

  const handleGridRowSelection = ({ node }) => {
    const { data } = node;
    const isSelected = node.isSelected();
    setSelectedRecords((prev) => {
      let updatedData = [...prev];
      if (prev.includes(data.id) && !isSelected) {
        updatedData = updatedData.filter((dt) => dt !== data.id);
      } else {
        if (isSelected) {
          updatedData.push(data.id);
        }
      }
      return updatedData;
    });
  };

  return (
    <StyledRightContainer>
      {toastInfo && <Alert {...toastInfo} onToastClose={handleToastClose} />}
      {isLoading && <Loader />}
      <h1>{translate("labels.reportsDetails")}</h1>
      <div className="report-row">
        <div className="report-row-col-4">
          <label>{translate("labels.resourceName")}</label>
          <Autocomplete
            options={formattedResourceDrpDwnOptions}
            id="resourceName"
            name="resourceName"
            value=""
            autoCompleteValue={resourceKey}
            onSelection={handleSelection}
            renderOption={(props, option) => {
              const { key, ...restItemProps } = props;
              return (
                <ResourceLabelContent
                  itemProps={restItemProps}
                  key={`${key}-${option.value}`}
                  option={option}
                />
              );
            }}
            onChange={updateResourceKey}
            loading={isResourceOptionsLoading}
          />
        </div>
        <div className="report-row-col-4 tma_form-control">
          <label>{translate("labels.startDate")}</label>
          {/* <Autocomplete
            multiple
            options={selectedWeeks}
            id="weekRange"
            name="weekRange"
            value=""
            disabled={selectedResource === null ? true : false}
            autoCompleteValue={selectedWeeks
              .filter(
                (week) => week.label !== "Select All" && week.selected === true
              )
              .map((week) => week.label)
              .join(", ")}
            disableCloseOnSelect={true}
            renderOption={(props, option) => {
              const { key, ...restItemProps } = props;
              return (
                <div
                  {...restItemProps}
                  key={`${key}-${option.label}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "0.5rem",
                  }}
                >
                  <Checkbox
                    checked={option.selected}
                    style={{ marginRight: "8px" }}
                    onChange={() => handleCheckboxChange(option)}
                  />
                  {option.label}
                </div>
              );
            }}
            onSelection={handleweekSelection}
          /> */}
          <DatePickerInput
            id={"startDate"}
            name="startDate"
            disabled={resourceCreatedOn === null ? true : false}
            value={startDate}
            onChange={(newDate) => {
              setStartDate(moment(newDate).format("YYYY-MM-DD"));
            }}
            disablePast={false}
            // minDate={resourceCreatedOn}
            maxDate={new Date()}
            // onBlur={formik.handleBlur}
            // error={
            //   formik.touched.startDate && Boolean(formik.errors.startDate)
            // }
            // helperText={formik.touched.startDate && formik.errors.startDate}
            // disabled={isReadOnlyMode}
          />
        </div>
        <div className="report-row-col-4">
          <label>{translate("labels.endDate")}</label>
          <DatePickerInput
            id={"endDate"}
            name="endDate"
            disabled={startDate === null ? true : false}
            value={endDate}
            onChange={(newDate) => {
              setEndDate(moment(newDate).format("YYYY-MM-DD"));
            }}
            disablePast={false}
            minDate={startDate}
            maxDate={new Date()}
            // onBlur={formik.handleBlur}
            // error={
            //   formik.touched.endDate && Boolean(formik.errors.endDate)
            // }
            // helperText={formik.touched.endDate && formik.errors.endDate}
            // disabled={isReadOnlyMode}
          />
        </div>
        <div className="report-row-col-4 p_0">
          <div className="search_btn">
            <button onClick={handleSearch}>Search</button>
            {rowData.length ? (
              <button
                onClick={handleDownload}
                style={{
                  marginLeft: "1rem",
                }}
              >
                Download
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="tma__table-container projectList">
        <Table
          columns={columnDefs}
          // rowSelection={"single"}
          rowData={rowData}
          showPagination={true}
          totalRecords={totalRecords}
          gridRef={gridRef}
          autoFit={true}
          onPaginationChange={handlePaginationChange}
          currentPage={page}
          pageSize={pageSize}
          onRowSelected={handleGridRowSelection}
        />
      </div>
    </StyledRightContainer>
  );
};
